import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { getAdminFbReports } from "@/actions/reportsAdmin";
import { Download, Facebook, TrendingUp, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface FbCampaignPerformance {
  slate_name: string;
  slate_id: number;
  city_name?: string;
  area_name?: string;
  campaigns: number;
  campaign_ids?: string[];
  campaign_names?: string[];
  form_ids?: string[];
  impressions: number;
  reach: number;
  results: number;
  spend: number;
  clicks?: number;
  cpr: number;
  ctr: number;
}

const formatNumber = (num: number): string => num?.toLocaleString() || "0";

type SortField = 'slate_name' | 'campaigns' | 'impressions' | 'reach' | 'results' | 'spend' | 'cpr' | 'ctr';
type SortOrder = 'asc' | 'desc';

const FbAdsReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [fbCampaignData, setFbCampaignData] = useState<FbCampaignPerformance[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortField>('slate_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Filter schools based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return fbCampaignData;
    const term = searchTerm.toLowerCase();
    return fbCampaignData.filter(school =>
      school.slate_name?.toLowerCase().includes(term) ||
      school.city_name?.toLowerCase().includes(term) ||
      school.area_name?.toLowerCase().includes(term)
    );
  }, [fbCampaignData, searchTerm]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async (sort_by: SortField = sortBy, sort_order: SortOrder = sortOrder) => {
    setLoading(true);
    try {
      const response = await getAdminFbReports({ from_date: fromDate, to_date: toDate, sort_by, sort_order });
      if (response.success) setFbCampaignData((response.data as FbCampaignPerformance[]) || []);
    } catch (error) {
      console.error("Error fetching FB reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    const newOrder: SortOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
    fetchData(field, newOrder);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const exportToCSV = () => {
    const headers = ["School Name", "City", "Area", "Campaign Names", "Form IDs", "Impressions", "Reach", "Results", "Spend", "CPR", "CTR"];
    const csvRows = [headers.join(",")];
    filteredData.forEach((s) => {
      csvRows.push([
        `"${s.slate_name?.replace(/_/g, " ")}"`,
        `"${s.city_name || ''}"`,
        `"${s.area_name || ''}"`,
        `"${s.campaign_names?.join('; ') || ''}"`,
        `"${s.form_ids?.join('; ') || ''}"`,
        s.impressions,
        s.reach,
        s.results,
        s.spend?.toFixed(2) || "0.00",
        s.cpr?.toFixed(2) || "0.00",
        s.ctr?.toFixed(2) || "0.00"
      ].join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `fb-campaign-performance-${fromDate}_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totals = useMemo(() => filteredData.reduce((acc, s) => ({
    campaigns: acc.campaigns + (s.campaigns || 0),
    impressions: acc.impressions + (s.impressions || 0),
    reach: acc.reach + (s.reach || 0),
    results: acc.results + (s.results || 0),
    spend: acc.spend + (s.spend || 0),
    clicks: acc.clicks + (s.clicks || 0),
  }), { campaigns: 0, impressions: 0, reach: 0, results: 0, spend: 0, clicks: 0 }), [filteredData]);

  const avgCpr = totals.results > 0 ? (totals.spend / totals.results).toFixed(2) : "0.00";
  const avgCtr = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : "0.00";

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader title="FB Ads Reports" subtitle="Facebook Ads campaign performance" icon={<TrendingUp className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={() => fetchData()} />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ReportHeader title="FB Ads Reports" subtitle="Facebook Ads campaign performance" icon={<TrendingUp className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={() => fetchData()} />

      <div className="flex-1 overflow-y-auto p-6">
        {fbCampaignData.length === 0 ? (
          <div className="text-center py-8">
            <Facebook className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No Facebook campaign data available</p>
            <button onClick={() => fetchData()} className="mt-4 px-4 py-2 bg-brandColor text-white rounded-md hover:bg-brandColorDark">Load Data</button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-blue-500">
                <div className="text-sm gray-text">Total Campaigns</div>
                <div className="text-2xl font-bold">{formatNumber(totals.campaigns)}</div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="text-sm gray-text">Total Impressions</div>
                <div className="text-2xl font-bold">{formatNumber(totals.impressions)}</div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="text-sm gray-text">Total Reach</div>
                <div className="text-2xl font-bold">{formatNumber(totals.reach)}</div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-orange-500">
                <div className="text-sm gray-text">Total Results</div>
                <div className="text-2xl font-bold">{formatNumber(totals.results)}</div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-red-500">
                <div className="text-sm gray-text">Total Spend</div>
                <div className="text-2xl font-bold">₹{formatNumber(Math.round(totals.spend))}</div>
              </div>
            </div>

            {/* FB Campaign Table */}
            <div className="background-container rounded-lg shadow">
              <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Facebook Ads Campaign Performance by School
                </h3>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search school..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-white/50 w-48"
                    />
                  </div>
                  <button onClick={exportToCSV} className="inline-flex items-center gap-2 px-4 py-2 bg-white text-brandColor text-sm font-medium rounded-lg hover:bg-gray-100">
                    <Download className="h-4 w-4" />Export to CSV
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th onClick={() => handleSort('slate_name')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none min-w-[200px] max-w-[250px]">
                        <span className="flex items-center">School Name<SortIcon field="slate_name" /></span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 min-w-[100px]">
                        City
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 min-w-[120px]">
                        Area
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 max-w-[180px]">
                        Campaign Names
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 max-w-[120px]">
                        Form IDs
                      </th>
                      <th onClick={() => handleSort('impressions')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none">
                        <span className="flex items-center">Impressions<SortIcon field="impressions" /></span>
                      </th>
                      <th onClick={() => handleSort('reach')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none">
                        <span className="flex items-center">Reach<SortIcon field="reach" /></span>
                      </th>
                      <th onClick={() => handleSort('results')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none">
                        <span className="flex items-center">Results<SortIcon field="results" /></span>
                      </th>
                      <th onClick={() => handleSort('spend')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none">
                        <span className="flex items-center">Spend<SortIcon field="spend" /></span>
                      </th>
                      <th onClick={() => handleSort('cpr')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none">
                        <span className="flex items-center">CPR ₹<SortIcon field="cpr" /></span>
                      </th>
                      <th onClick={() => handleSort('ctr')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 cursor-pointer hover:bg-gray-100 select-none">
                        <span className="flex items-center">CTR %<SortIcon field="ctr" /></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((school) => (
                      <tr key={school.slate_id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 min-w-[200px] max-w-[250px]">
                          <div className="truncate" title={school.slate_name?.replace(/_/g, " ")}>{school.slate_name?.replace(/_/g, " ")}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{school.city_name || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{school.area_name || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-[180px]">
                          <div className="space-y-1">
                            {school.campaign_names?.map((name: string, idx: number) => (
                              <div key={idx} className="text-xs truncate" title={name}>{name}</div>
                            )) || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-[120px]">
                          <div className="space-y-1">
                            {school.form_ids?.map((formId: string, idx: number) => (
                              <div key={idx} className="text-xs truncate" title={formId}>{formId}</div>
                            )) || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(school.impressions)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(school.reach)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(school.results)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">₹{school.spend?.toFixed(2) || "0.00"}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">₹{school.cpr?.toFixed(2) || "0.00"}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{school.ctr?.toFixed(2) || "0.00"}%</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0 z-10">
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">Total ({filteredData.length} schools)</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">-</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">-</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">-</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">-</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">{formatNumber(totals.impressions)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">{formatNumber(totals.reach)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">{formatNumber(totals.results)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">₹{totals.spend.toFixed(2)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">₹{avgCpr}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">{avgCtr}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FbAdsReport;
